import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import { useAppSelector } from '../../redux/hooks';
import { ClinicLocationService } from '../../services/clinic-location.service';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import ClinicLocationInfo from './ClinicLocationInfo';

const SelectedClinicInfo = () => {
  const { t } = useTranslation();
  const selectedClinic: ClinicLocation | undefined = useAppSelector(
    (state) => state.user.selectedClinicLocation
  );
  const {
    data: clinicLocationInfo,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['clinic-location', selectedClinic?.id],
    queryFn: () =>
      ClinicLocationService.getClinicLocationById(selectedClinic!.id!),
    enabled: !!selectedClinic?.id,
  });

  const renderContent = () => {
    if (!selectedClinic?.id) {
      return (
        <OvRecordDetailsCanvas>
          <OvListStateMessage
            icon={<LocalHospitalIcon />}
            title={t('clinicLocationDetails.states.noSelectionTitle')}
            description={t(
              'clinicLocationDetails.states.noSelectionDescription'
            )}
          />
        </OvRecordDetailsCanvas>
      );
    }

    if (clinicLocationInfo) {
      return (
        <OvRecordDetailsCanvas>
          <OvRecordDetailsSection
            title={clinicLocationInfo.name ?? selectedClinic.name}
          >
            <ClinicLocationInfo selectedClinicLocation={clinicLocationInfo} />
          </OvRecordDetailsSection>
        </OvRecordDetailsCanvas>
      );
    }

    return error ? (
      <OvRecordDetailsErrorState
        title={t('clinicLocationDetails.details.errorTitle')}
        error={error}
        onRetry={() => refetch()}
      />
    ) : null;
  };

  return (
    <OvRecordDetailsPage aria-busy={isFetching}>
      {renderContent()}
      {isFetching && <OvLoadingIndicator position="fixed" />}
    </OvRecordDetailsPage>
  );
};

export default SelectedClinicInfo;
